import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  getTestdataHistory,
  deleteTestdataGeneration,
  getTestdataGeneration,
  downloadTestdataXml,
  downloadTestdataPdf,
  type TestdataGenerationSummary,
} from '../api/testdataHistory'

const DOC_TYPE_LABELS: Record<string, string> = {
  invoice: 'Factuur',
  order_response: 'Orderbevestiging',
  despatch_advice: 'Pakbon',
  credit_note: 'Creditnota',
}

const FORMAT_LABELS: Record<string, string> = {
  sales005: 'SALES005',
  ubl21: 'UBL 2.1',
  cii: 'CII D22B',
}

const DOC_TYPE_COLORS: Record<string, string> = {
  invoice: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  order_response: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  despatch_advice: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  credit_note: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
}

function formatAmount(amount: number | null): string {
  if (amount === null) return '—'
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount)
}

interface Props {
  isAdmin: boolean
  onLoadIntoForm: (params: Record<string, unknown>) => void
}

export function TestdataHistoryPanel({ isAdmin, onLoadIntoForm }: Props) {
  const [records, setRecords] = useState<TestdataGenerationSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [loadingId, setLoadingId] = useState<number | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  useEffect(() => {
    getTestdataHistory()
      .then(setRecords)
      .catch(e => setError(e instanceof Error ? e.message : 'Laden mislukt'))
      .finally(() => setLoading(false))
  }, [])

  const filtered = records.filter(r => {
    if (!query.trim()) return true
    const q = query.toLowerCase()
    return [
      r.document_number,
      r.supplier_name,
      r.buyer_name,
      DOC_TYPE_LABELS[r.document_type],
      isAdmin ? r.created_by : null,
    ].some(s => s?.toLowerCase().includes(q))
  })

  const handleLoadIntoForm = async (id: number) => {
    setLoadingId(id)
    setError(null)
    try {
      const detail = await getTestdataGeneration(id)
      if (detail.input_params) {
        onLoadIntoForm(detail.input_params)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Herladen mislukt')
    } finally {
      setLoadingId(null)
    }
  }

  const handleDelete = async (id: number, docNumber: string) => {
    if (!window.confirm(`Generatie "${docNumber}" verwijderen?`)) return
    setDeletingId(id)
    setError(null)
    try {
      await deleteTestdataGeneration(id)
      setRecords(rs => rs.filter(r => r.id !== id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Verwijderen mislukt')
    } finally {
      setDeletingId(null)
    }
  }

  const handleDownloadXml = async (r: TestdataGenerationSummary) => {
    setDownloadingId(r.id)
    setError(null)
    try {
      await downloadTestdataXml(r.id, r.document_number, r.document_type)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download mislukt')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleDownloadPdf = async (r: TestdataGenerationSummary) => {
    setDownloadingId(r.id)
    setError(null)
    try {
      await downloadTestdataPdf(r.id, r.document_number, r.document_type)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download mislukt')
    } finally {
      setDownloadingId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 dark:text-gray-500">
        <svg className="animate-spin w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
        Laden…
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Zoekbalk */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={isAdmin ? 'Zoek op nummer, partij of gebruiker…' : 'Zoek op nummer of partij…'}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          {filtered.length} van {records.length} generaties
        </span>
      </div>

      {/* Foutmelding */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-xl px-4 py-3 text-sm flex items-start justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-4 text-red-400 hover:text-red-600">×</button>
        </div>
      )}

      {/* Lege staat */}
      {filtered.length === 0 && (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <svg className="mx-auto w-10 h-10 mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="font-medium">{records.length === 0 ? 'Nog geen generaties' : 'Geen resultaten voor deze zoekopdracht'}</p>
          <p className="text-xs mt-1">{records.length === 0 ? 'Gegenereerde documenten verschijnen hier automatisch.' : 'Pas de zoekterm aan.'}</p>
        </div>
      )}

      {/* Tabel */}
      {filtered.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">#</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Datum</th>
                {isAdmin && <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Gebruiker</th>}
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Type</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Formaat</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Documentnummer</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Leverancier</th>
                <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Koper</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Regels</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Totaal</th>
                <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {filtered.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500 text-xs">#{r.id}</td>
                  <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap text-xs">
                    {format(new Date(r.created_at), 'd MMM yyyy HH:mm', { locale: nl })}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2.5">
                      <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded px-1.5 py-0.5">
                        {r.created_by}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-2.5">
                    <span className={`text-xs font-medium rounded px-2 py-0.5 ${DOC_TYPE_COLORS[r.document_type] ?? 'bg-gray-100 text-gray-700'}`}>
                      {DOC_TYPE_LABELS[r.document_type] ?? r.document_type}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                      {FORMAT_LABELS[r.xml_format] ?? r.xml_format}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200 whitespace-nowrap text-xs">
                    {r.document_number}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 text-xs max-w-[160px] truncate">
                    {r.supplier_name ?? '—'}
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 dark:text-gray-300 text-xs max-w-[160px] truncate">
                    {r.buyer_name ?? '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-600 dark:text-gray-300 text-xs">
                    {r.line_count}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-800 dark:text-gray-200 text-xs whitespace-nowrap">
                    {r.total_amount !== null ? formatAmount(r.total_amount) : '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-1">
                      {/* Download XML */}
                      <button
                        onClick={() => handleDownloadXml(r)}
                        disabled={downloadingId === r.id}
                        title="XML downloaden"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 transition-colors disabled:opacity-40"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>

                      {/* Download PDF */}
                      {r.has_pdf && (
                        <button
                          onClick={() => handleDownloadPdf(r)}
                          disabled={downloadingId === r.id}
                          title="PDF downloaden"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors disabled:opacity-40"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </button>
                      )}

                      {/* Herladen in formulier */}
                      <button
                        onClick={() => handleLoadIntoForm(r.id)}
                        disabled={loadingId === r.id}
                        title="Herladen in formulier"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 dark:hover:text-green-400 transition-colors disabled:opacity-40"
                      >
                        {loadingId === r.id ? (
                          <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        )}
                      </button>

                      {/* Verwijderen */}
                      <button
                        onClick={() => handleDelete(r.id, r.document_number)}
                        disabled={deletingId === r.id}
                        title="Verwijderen"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors disabled:opacity-40"
                      >
                        {deletingId === r.id ? (
                          <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                          </svg>
                        ) : (
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
